<template>
  <Card :class="isMagazine ? 'p-0 border-none' : 'p-4 md:px-7'">
    <form action="#" @submit.prevent="addToCart">
      <label class="kft-buy-box__label text-sm mb-2 inline-flex">
        {{ t('giftcard.select') }}
      </label>

      <div
        data-test-id="gifcard-amount-options"
        class="giftcard-buy-box__options mb-4 flex flex-wrap gap-2"
      >
        <div
          v-for="amount in amounts"
          :key="amount"
          class="chip-amount text-xs font-medium h-[34px] md:h-[32px] px-3 py-2 flex-auto text-center justify-center flex items-center cursor-pointer ease duration-300 rounded-3xl border hover:border-teal-900 hover:text-teal-900 hover:bg-teal-50 text-gray-700"
          :class="[
            selectedAmount === amount
              ? 'border-[#047A88] text-[#047A88] bg-teal-50'
              : 'border-gray-300',
          ]"
          @click="selectAmount(amount)"
        >
          {{ formatMoney(amount, locale, undefined, 0) }}
        </div>
      </div>

      <label class="kft-buy-box__label text-sm mb-2 inline-flex">
        {{ t('giftcard.input') }}
      </label>
      <div class="relative">
        <input
          type="number"
          data-test-id="giftcard-amount-input"
          class="font-regular w-full h-[42px] px-4 border ease duration-300 border-secondary outline-none focus:border-primary-light rounded-lg"
          :class="error.visible ? 'border-red-500' : ''"
          :value="inputValue"
          min="15"
          max="1000000"
          step="1"
          @input="handleInput"
          @focus="handleInputFocus"
          @blur="handleInputBlur"
        />
        <div
          :class="[
            'pointer-events-none absolute top-[1px] left-[1px] right-[1px] bottom-[1px]',
            'flex items-center px-4 ease duration-300 bg-white rounded-lg z-1',
            isInputFocused ? 'opacity-0 invisible' : '',
          ]"
        >
          {{ formatMoney(selectedAmount, locale, undefined, 0) }}
        </div>
        <div
          class="z-[3] absolute right-[2px] top-0 bottom-0 my-auto w-9 h-9 flex items-center justify-center bg-white rounded-lg cursor-pointer hover:op-70"
          @click="clearAmount"
        >
          <ClearIcon />
        </div>
      </div>
      <div v-if="error.visible" class="text-danger-active text-sm mt-2">
        {{ error.message }}
      </div>

      <Button class="w-full mt-2" data-test-id="buy-giftcard" type="submit">
        {{ t('giftcard.bookAction') }}
      </Button>
    </form>
  </Card>
</template>

<script lang="ts" setup>
import ClearIcon from '~/public/icons/x.svg?component'

const { t, locale } = useI18n()
const { addItem } = useCartWithSubdomainLogic()
const { toggleCartSidebar } = useUiState('general-instance')

const subdomain = useSubdomain()
const { subdomainData: partnerData } = subdomain.value
  ? await useSubdomainData()
  : { subdomainData: ref(undefined) }

const supplierId = computed(() => supplierGetters.getId(partnerData.value))
const supplierName = computed(() => supplierGetters.getName(partnerData.value))

const props = withDefaults(
  defineProps<{
    isMagazine?: boolean
  }>(),
  {
    isMagazine: false,
  }
)

// Available options
const amounts = ref([
  parseInt(t('giftcard.option_1')),
  parseInt(t('giftcard.option_2')),
  parseInt(t('giftcard.option_3')),
  parseInt(t('giftcard.option_4')),
])
// Minimal amount for input
const minimalAmount = 15
const maximumAmount = 200
// Selected option
const selectedAmount = toRef(amounts.value[0])
// Value we set in input
const inputValue = ref(amounts.value[0])
// Check if input focused
const isInputFocused = ref(false)
// Check if error is visible
const error = ref({
  visible: false,
  message: '',
})
const selectAmount = (amount: number) => {
  selectedAmount.value = amount
  inputValue.value = amount
}

const handleInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  inputValue.value = input.value
  selectedAmount.value = parseInt(inputValue.value)
  error.value.visible = false
}

const checkError = () => {
  let isInputValid = true
  const parsedInput = parseInt(inputValue.value)

  if (isNaN(parsedInput)) {
    selectedAmount.value = 0
    inputValue.value = 0
    isInputValid = false
  } else if (parsedInput < minimalAmount || parsedInput > maximumAmount) {
    isInputValid = false
  }

  if (!isInputValid) {
    error.value.visible = true
    error.value.message = t('giftcard.inputError')
  }

  return isInputValid
}

const handleInputFocus = () => {
  isInputFocused.value = true
}

const handleInputBlur = () => {
  isInputFocused.value = false
  checkError()
}

const clearAmount = () => {
  inputValue.value = minimalAmount
  selectedAmount.value = minimalAmount

  error.value.visible = false
}

const getSupplierName = computed(() => {
  if (supplierName.value) {
    return supplierName.value
  }

  if (props.isMagazine) {
    return 'konfetti magazine'
  }

  return 'konfetti'
})

const addToCart = () => {
  if (!checkError()) return

  const cartItem = parseGiftcardWithoutEvent(
    {
      amount: `${selectedAmount.value}00`,
      currency: 'EUR',
      formatted: `${parseInt(selectedAmount.value + '', 10).toFixed(2)} €`,
    },
    t('cart.giftcardTitle'),
    supplierId.value,
    'giftcard_' + selectedAmount.value,
    getSupplierName.value
  )

  addItem(cartItem)
  // sendAddToCart(null, true, cartItem) TODO: handle GTM
  toggleCartSidebar()
}
</script>

<style lang="postcss" scoped>
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
</style>
